// v0.5.9e / v0.5.7 Stage 5 — THE single frontend source of filing readiness.
// Mirrors backend engine/filing_readiness.py::compute_readiness. Every
// banner / strip / chip / indicator that makes a "can this be filed?" claim
// MUST derive it from here — no component recomputes readiness from raw
// gates (ARCHITECTURE_INVARIANTS #4). `ready` is derived from `blocking`, so
// two indicators built from this verdict can never contradict.

// Single source for "did the calc-level reconciliation (R1-R4) fail?" —
// R1-R4 `deltas` ONLY. NEVER `recon.do_not_file` / `rule_checks_failed`:
// those are a calc-TIME snapshot that also folds in the mutable review-queue
// count, so they go stale once reviews are acknowledged and render a false
// "Cannot file" that contradicts this verdict (READY/Cannot-file regression,
// v0.5.7). Every component that shows a reconciliation verdict imports this.
export const reconciliationFailed = (recon) =>
  (recon?.deltas || []).length > 0;

// Single source for "how many Family-B rows still need a per-row decision?"
// GET /review-grouped's GroupedResponse has never carried a top-level
// `unresolved_count` field (verified against backend/api/_review_models.py)
// — every caller that read `review?.unresolved_count` was always reading
// `undefined`, so `review_flags_ok` below silently evaluated to "resolved"
// regardless of real open Family-B rows. The real per-group figures are
// `count` (total rows on that flag) and `acknowledged_count` (rows an
// operator has already acked for that exact flag_type) — mirrors how the
// backend's own api._review_gate.open_family_b_summary() counts a row
// resolved (present in review_acknowledgements for that flag_type).
// A2-640 (operator 2026-11-03): open rows of a country this calculation files
// no return for (g.off_period, from the server) do not hold it. ONE count of
// "still to answer here", read by Step 4, the status gate, Step 6 and the cards.
export const offPeriodRows = (g) => Object.values(g?.off_period || {}).reduce((a, n) => a + n, 0);
export const familyBOpenHere = (g) => (g?.family === "B"
  ? Math.max(0, (g.count || 0) - (g.acknowledged_count || 0) - offPeriodRows(g)) : 0);
export const reviewUnresolvedCount = (review) =>
  (review?.groups || []).reduce((sum, g) => sum + familyBOpenHere(g), 0);

export const READINESS_GATES = {
  reconciliation_ok: "Calc reconciliation (R1-R4) must pass",
  review_flags_ok: "All blocking review flags must be resolved",
  coverage_ok: "All registration-coverage gaps must be acknowledged",
  // v0.5.7 Stage 5 — AMBER gate: required manual-input boxes (deductible
  // input VAT per return + ack-warning boxes) must be entered or confirmed.
  needs_input_ok: "All required manual-input boxes must be entered or confirmed",
  // v0.6.6 — this calc was replaced by a later supersede (backend stamps
  // `superseded_by_calc_id`; routes_filing_package.py 409s on it). A
  // bookmarked/back-navigated URL can still land an operator on the old
  // calc's Step 4/5, where this verdict was the only readiness indicator
  // that never looked at supersession — it could show "Ready to file" for
  // a calc that's no longer the live one for this period.
  supersession_ok: "This filing has been superseded — view the current calculation",
};

// UI severity per gate (mirrors backend GATE_SEVERITY). Readiness is a pure
// AND regardless of severity; this only drives strip colour.
export const GATE_SEVERITY = {
  reconciliation_ok: "red",
  review_flags_ok: "red",
  coverage_ok: "red",
  needs_input_ok: "amber",
  supersession_ok: "red",
};

// inputs: { calc, review, coverageOpenGaps, needsInputPending }
// Returns { ready, blocking:[{key,label,severity}], gates:{key:bool},
//           redBlocking:[…], needsInput:[…] }.
export const computeFilingReadiness = (
  { calc, review, coverageOpenGaps, needsInputPending } = {},
) => {
  const gates = {
    reconciliation_ok: !reconciliationFailed(calc?.reconciliation),
    review_flags_ok: reviewUnresolvedCount(review) === 0,
    coverage_ok: (coverageOpenGaps || 0) === 0,
    needs_input_ok: (needsInputPending || 0) === 0,
    supersession_ok: !calc?.superseded_by_calc_id,
  };
  const blocking = Object.entries(gates)
    .filter(([, ok]) => !ok)
    .map(([key]) => ({ key, label: READINESS_GATES[key], severity: GATE_SEVERITY[key] }));
  return {
    ready: blocking.length === 0,
    blocking,
    gates,
    redBlocking: blocking.filter((b) => b.severity === "red"),
    needsInput: blocking.filter((b) => b.severity === "amber"),
  };
};

export default computeFilingReadiness;
